import React from "react";
import type { ScopeMember } from "../../../../models";
import type { LeafRow } from "../boardLayout";
import { TRAIN_LEAF_KEY } from "../../PullPlanGrid/train/trainReconcile";
import LevelPill from "../LevelPill/LevelPill";
import "./ZoneHeader.css";

type DropLeaf = { key: string; pos: "above" | "below" } | null;

interface Props {
  leaf: LeafRow;
  readOnly: boolean;
  selectedLeafKey: string | null;
  onSelectLeaf?: (leafKey: string | null) => void;

  /** Row reorder drag state (shared with the parent's reorder commit). */
  dragLeaf: string | null;
  setDragLeaf: (key: string | null) => void;
  dropLeaf: DropLeaf;
  setDropLeaf: (next: DropLeaf | ((prev: DropLeaf) => DropLeaf)) => void;
  dropRowOn: (targetKey: string) => void;
  onRowOrderChange?: (keys: string[]) => void;

  /** Inline rename of user-added rows. */
  renamingRow: string | null;
  setRenamingRow: (key: string | null) => void;
  onRenameRow?: (key: string, label: string) => void;
  onDeleteRow?: (key: string) => void;

  /** True ⇒ this leaf is a user-added extra row (rename/delete tools). */
  isExtra: boolean;

  /** Pull target chip ("▎6/8"). */
  target?: { code: string; date: string | null };
  tgtDate: (iso: string | null) => string;

  /** Takt context + level controls. */
  taktContextByLeaf?: Map<string, { takt_a: string | null; takt_b: string | null }>;
  levelByLeaf?: Map<string, string | null>;
  miscodeCountByLeaf?: Map<string, number>;
  notaktLeaves?: Set<string>;
  taktCMenu?: string[];
  onSetLeafLevel?: (leafKey: string, level: string) => void;
  onToggleNotakt?: (leafKey: string) => void;

  /** Zone-level selection control. */
  onSelectionChange?: (next: Set<number>) => void;
  editable: (m: ScopeMember) => boolean;
}

/** The header row of a board zone (WBS leaf): reorder handle, name + inline
 *  rename, Takt context + level pill, train-lane actions, and the centered
 *  "Select zone" control. Rendered once per leaf by WbsBoardView. The lane
 *  add/remove controls live in a footer below the last lane (WbsBoardView). */
const ZoneHeader: React.FC<Props> = ({
  leaf,
  readOnly,
  selectedLeafKey,
  onSelectLeaf,
  dragLeaf,
  setDragLeaf,
  dropLeaf,
  setDropLeaf,
  dropRowOn,
  onRowOrderChange,
  renamingRow,
  setRenamingRow,
  onRenameRow,
  onDeleteRow,
  isExtra,
  target,
  tgtDate,
  taktContextByLeaf,
  levelByLeaf,
  miscodeCountByLeaf,
  notaktLeaves,
  taktCMenu,
  onSetLeafLevel,
  onToggleNotakt,
  onSelectionChange,
  editable,
}) => {
  return (
    <div
      className={
        "wbs-board-zone-head" +
        (dropLeaf?.key === leaf.key ? ` wbs-board-zone-head--drop-${dropLeaf.pos}` : "") +
        (onSelectLeaf ? " wbs-board-zone-head--selectable" : "")
      }
      onClick={(e) => {
        if (!onSelectLeaf) return;
        // Ignore clicks that land on the row's own controls (rename,
        // reorder handle, level pill, lane/delete buttons).
        if ((e.target as HTMLElement).closest("button, input, [draggable=true]")) return;
        onSelectLeaf(leaf.key === selectedLeafKey ? null : leaf.key);
      }}
      onDragOver={(e) => {
        if (!dragLeaf) return;
        e.preventDefault();
        const r = e.currentTarget.getBoundingClientRect();
        const pos = e.clientY < r.top + r.height / 2 ? "above" : "below";
        setDropLeaf((prev) =>
          prev?.key === leaf.key && prev.pos === pos ? prev : { key: leaf.key, pos },
        );
      }}
      onDrop={(e) => {
        if (!dragLeaf) return;
        e.preventDefault();
        dropRowOn(leaf.key);
      }}
    >
      <div className="wbs-board-zone-head-in">
        {leaf.key !== TRAIN_LEAF_KEY && !readOnly && onRowOrderChange && (
          <span
            className="wbs-board-row-handle"
            title="Drag to reorder this zone"
            draggable
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", leaf.key);
              setDragLeaf(leaf.key);
            }}
            onDragEnd={() => {
              setDragLeaf(null);
              setDropLeaf(null);
            }}
          >
            ⠿
          </span>
        )}
        <div className="wbs-board-zone-title">
          {renamingRow === leaf.key && onRenameRow ? (
            <input
              type="text"
              className="wbs-board-rename-input"
              autoFocus
              defaultValue={leaf.label}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                if (e.key === "Escape") setRenamingRow(null);
              }}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== leaf.label) onRenameRow(leaf.key, v);
                setRenamingRow(null);
              }}
            />
          ) : (
            <>
              <div className="wbs-board-zone-nameline">
                <span className="wbs-board-leaf-name" title={leaf.path.join(" › ")}>
                  {leaf.label}
                </span>
                {leaf.key === TRAIN_LEAF_KEY && (
                  <span
                    className={
                      "wbs-board-train-hint" +
                      (leaf.key === selectedLeafKey ? " wbs-board-train-hint--on" : "")
                    }
                    title="Select the train to edit its ties in the logic editor below"
                  >
                    {leaf.key === selectedLeafKey ? "▾ ties below" : "click to view ties →"}
                  </span>
                )}
              </div>
              <div className="wbs-board-zone-sub">
                {!isExtra && leaf.key !== TRAIN_LEAF_KEY && (
                  <>
                    {(() => {
                      const ctx = taktContextByLeaf?.get(leaf.key);
                      const label = [ctx?.takt_a, ctx?.takt_b]
                        .filter(Boolean)
                        .join(" · ");
                      return label ? (
                        <span
                          className="wbs-board-leaf-takt"
                          title="Takt section · zone — derived from this leaf's work"
                        >
                          {label}
                        </span>
                      ) : null;
                    })()}
                    {notaktLeaves?.has(leaf.key)
                      ? onSetLeafLevel && (
                          <button
                            type="button"
                            className="wbs-board-notakt-chip"
                            disabled={readOnly}
                            title="Not Takt work — sequence only, never placed on a level. Click to make it a Takt zone."
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!readOnly) onToggleNotakt?.(leaf.key);
                            }}
                          >
                            ⚙ not Takt
                          </button>
                        )
                      : (onSetLeafLevel ||
                          (levelByLeaf?.get(leaf.key) ?? null)) && (
                          <LevelPill
                            level={levelByLeaf?.get(leaf.key) ?? null}
                            miscodeCount={miscodeCountByLeaf?.get(leaf.key) ?? 0}
                            menu={taktCMenu ?? []}
                            readOnly={readOnly || !onSetLeafLevel}
                            onSet={
                              onSetLeafLevel
                                ? (lvl) => onSetLeafLevel(leaf.key, lvl)
                                : () => undefined
                            }
                          />
                        )}
                  </>
                )}
                {isExtra && !readOnly && (
                  <span className="wbs-board-rowtools">
                    <button
                      type="button"
                      className="wbs-board-rowtool"
                      title="Rename this zone"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => setRenamingRow(leaf.key)}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="wbs-board-rowtool"
                      title="Delete this zone (bars in it go back home)"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => onDeleteRow?.(leaf.key)}
                    >
                      🗑
                    </button>
                  </span>
                )}
                {target && target.date && (
                  <span
                    className="wbs-board-zone-target"
                    title="The pull target date this zone builds toward"
                  >
                    ▎{tgtDate(target.date)}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
        {leaf.key !== TRAIN_LEAF_KEY && !readOnly && onSelectionChange && (
          <button
            type="button"
            className="wbs-board-zone-btn"
            title="Select every activity in this zone"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              const ids = new Set<number>();
              for (const b of leaf.bars) {
                if (editable(b.member)) ids.add(b.member.activity_id);
              }
              onSelectionChange(ids);
            }}
          >
            Select zone
          </button>
        )}
        <span className="wbs-board-zone-spacer" />
      </div>
    </div>
  );
};

export default ZoneHeader;
